import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
  title: "", subtitle: "", cover_image_url: "", description: "", excerpt: "",
  age_range: "", publish_date: "", amazon_url: "", barnes_noble_url: "",
  direct_url: "", other_url: "",
  coloring_page_url: "",
  coloring_pages: [],
  coloring_book_url: "",
  featured: false,
};

export default function AdminBooks() {
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | book object
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/books").then(({ data }) => setBooks(data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setEditing("new"); };
  const startEdit = (b) => { setForm({ ...empty, ...b }); setEditing(b); };
  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at;
      if (editing === "new") {
        await api.post("/books", payload);
        toast.success("Book added.");
      } else {
        await api.put(`/books/${editing.id}`, payload);
        toast.success("Book updated.");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    try {
      await api.delete(`/books/${b.id}`);
      toast.success("Book removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  return (
    <div data-testid="admin-books-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h1 className="font-fredoka text-4xl" style={{ color: "#2D3055" }}>Books</h1>
          <p className="mt-1" style={{ color: "#4A4E6D" }}>Add, edit, and curate your little library.</p>
        </div>
        <button onClick={startNew} className="btn-terracotta" data-testid="add-book-btn"><Plus size={18} /> Add book</button>
      </div>

      <div className="space-y-3">
        {books.map((b) => (
          <div key={b.id} className="warm-card p-4 flex items-center gap-4" data-testid={`admin-book-${b.id}`}>
            <img src={b.cover_image_url} alt="" className="w-16 h-20 object-cover rounded-xl" />
            <div className="flex-1 min-w-0">
              <p className="font-fredoka text-lg truncate" style={{ color: "#2D3055" }}>{b.title}</p>
              <p className="text-xs" style={{ color: "#7E82A1" }}>{b.age_range} {b.featured && "• ⭐ Featured"}</p>
            </div>
            <button onClick={() => startEdit(b)} className="btn-ghost-warm text-xs" data-testid={`edit-book-${b.id}`}><Pencil size={14} /> Edit</button>
            <button onClick={() => remove(b)} className="btn-ghost-warm text-xs" style={{ color: "#c1554b" }} data-testid={`delete-book-${b.id}`}><Trash2 size={14} /> Delete</button>
          </div>
        ))}
        {books.length === 0 && <p className="font-nunito" style={{ color: "#7E82A1" }}>No books yet. Add your first!</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(61,64,91,0.5)" }} data-testid="book-modal">
          <div className="warm-card w-full max-w-2xl max-h-[90vh] overflow-auto p-8 relative">
            <button onClick={close} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#E7EEFF]" data-testid="book-modal-close"><X /></button>
            <h2 className="font-fredoka text-3xl mb-6" style={{ color: "#2D3055" }}>
              {editing === "new" ? "Add a new book" : "Edit book"}
            </h2>
            <form onSubmit={save} className="space-y-4">
              {[
                ["title", "Title", "text", true],
                ["subtitle", "Subtitle", "text", false],
                ["cover_image_url", "Cover image URL", "url", true],
                ["age_range", "Age range (e.g. Ages 3-7)", "text", false],
                ["publish_date", "Publish date", "text", false],
                ["amazon_url", "Amazon URL", "url", false],
                ["barnes_noble_url", "Barnes & Noble URL", "url", false],
                ["direct_url", "Direct buy URL", "url", false],
                ["other_url", "Other retailer URL", "url", false],
                ["coloring_page_url", "Coloring page image URL (single page — legacy)", "url", false],
                ["coloring_book_url", "Coloring book PDF URL (whole-book download)", "url", false],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>{label}</label>
                  <input
                    type={type}
                    required={required}
                    value={form[k] || ""}
                    onChange={upd(k)}
                    className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none"
                    style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                    data-testid={`book-field-${k}`}
                  />
                </div>
              ))}
              <div>
                <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Description</label>
                <textarea required rows={4} value={form.description} onChange={upd("description")}
                  className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none resize-none"
                  style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                  data-testid="book-field-description"
                />
              </div>
              <div>
                <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Excerpt</label>
                <textarea rows={3} value={form.excerpt} onChange={upd("excerpt")}
                  className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none resize-none"
                  style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                  data-testid="book-field-excerpt"
                />
              </div>
              <label className="flex items-center gap-2 font-fredoka text-sm" style={{ color: "#2D3055" }}>
                <input type="checkbox" checked={form.featured} onChange={upd("featured")} data-testid="book-field-featured" />
                Featured on homepage
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn-terracotta" data-testid="book-save-btn">
                  {saving ? "Saving…" : editing === "new" ? "Add book" : "Save changes"}
                </button>
                <button type="button" onClick={close} className="btn-ghost-warm" data-testid="book-cancel-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
